import { useMemo } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import { useI18n } from '@xdocs/ui';
import type { TranslateVars } from '@xdocs/i18n';
import type { AdminCatalogKey } from './admin.i18n.js';
import { ADMIN_CATALOG } from './admin.i18n.js';

export type AdminMessageId = `admin:${AdminCatalogKey}`;

const PREFIX_LENGTH = 'admin:'.length;

/** `t()` for the stop-gap `admin:` scope (see `admin.i18n.ts`). Shares
 * `useI18n()`'s current locale so it stays in lockstep with the rest of the
 * app — never a second locale source. */
export function useAdminT(): { t: (id: AdminMessageId, vars?: TranslateVars) => string } {
  const { locale } = useI18n();

  const t = useMemo(() => {
    return (id: AdminMessageId, vars?: TranslateVars): string => {
      const key = id.slice(PREFIX_LENGTH) as AdminCatalogKey;
      const raw = ADMIN_CATALOG[locale][key] ?? ADMIN_CATALOG.en[key];
      if (!vars) return raw;
      const formatted = new IntlMessageFormat(raw, locale).format(vars);
      if (formatted == null) return '';
      if (typeof formatted === 'string') return formatted;
      return Array.isArray(formatted) ? formatted.map(String).join('') : String(formatted);
    };
  }, [locale]);

  return { t };
}
